import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, refreshSession } from "@/lib/api";
import { appSessionKey } from "@/lib/appContext";
import { useConsent } from "@/store/consent";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const consent = useConsent();
  const [user, setUser] = useState(null); // null=loading, false=logged out, obj=authed
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (requestError) {
      let previousSession = false;
      try { previousSession = sessionStorage.getItem(appSessionKey()) === "1"; } catch { /* storage blocked */ }
      if (previousSession && requestError.response?.status === 401) {
        setError("session_expired");
      }
      setUser(false);
    }
  }, []);

  useEffect(() => {
    if (consent?.active && !consent.canUseNecessary) {
      setUser(false);
      return;
    }
    refresh();
  }, [consent?.active, consent?.canUseNecessary, refresh]);

  // Session expired for real (refresh cookie invalid/revoked) → go back to login with a clear message.
  useEffect(() => {
    const onExpired = () => {
      let previousSession = false;
      try { previousSession = sessionStorage.getItem(appSessionKey()) === "1"; } catch { /* storage blocked */ }
      if (previousSession) setError("session_expired");
      setUser(false);
    };
    window.addEventListener("owlico:session-expired", onExpired);
    return () => window.removeEventListener("owlico:session-expired", onExpired);
  }, []);

  // Proactive renewal while the app is open: every 6h and whenever the tab becomes visible again,
  // so long batch reviews never hit an expired 12h access cookie mid-work.
  useEffect(() => {
    if (!user) return undefined;
    const renew = () => refreshSession().catch(() => {});
    const id = setInterval(renew, 6 * 60 * 60 * 1000);
    const onVisible = () => {
      if (document.visibilityState === "visible") renew();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user]);

  // Storage may be blocked by browser privacy settings; a successful server login must never be reported as a failure because of it.
  const rememberSession = () => { try { sessionStorage.setItem(appSessionKey(), "1"); } catch { /* ignore */ } };

  const login = async (email, password) => {
    setError("");
    let data;
    try {
      ({ data } = await api.post("/auth/login", { email: String(email).trim().toLowerCase(), password: String(password).trim() }));
    } catch (requestError) {
      if (requestError.response?.status === 401) {
        setError("invalid_credentials");
      } else if (requestError.response?.status === 429) {
        setError("locked_out");
      } else if (!requestError.response || [502, 503, 504].includes(requestError.response?.status)) {
        setError("temporary_problem");
      } else {
        setError(formatApiErrorDetail(requestError.response?.data?.detail) || "temporary_problem");
      }
      return false;
    }
    if (data.mfa_required) return { mfa: true, token: data.mfa_token };
    rememberSession();
    setUser(data.user);
    return true;
  };

  const loginMfa = async (mfaToken, code) => {
    setError("");
    let data;
    try {
      ({ data } = await api.post("/auth/login/2fa", { mfa_token: mfaToken, code }));
    } catch (requestError) {
      const st = requestError.response?.status;
      setError(st === 401 || st === 422 ? "invalid_code" : st === 429 ? "locked_out" : !requestError.response || [502, 503, 504].includes(st) ? "temporary_problem" : formatApiErrorDetail(requestError.response?.data?.detail) || "temporary_problem");
      return false;
    }
    rememberSession();
    setUser(data.user);
    return true;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    try { sessionStorage.removeItem(appSessionKey()); } catch { /* ignore */ }
    setUser(false);
  };

  const updatePrefs = async (patch) => {
    const { data } = await api.patch("/auth/me", patch);
    setUser(data);
    return data;
  };

  const hasModule = (m) => !!user && (user.role === "admin" || (user.modules || []).includes(m));

  return (
    <AuthCtx.Provider value={{ user, error, login, loginMfa, logout, updatePrefs, refresh, hasModule }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
